import React, { useState, useEffect, useRef } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import AccordionBox from "components/Layouts/AccordionBox";
import Row from "./Row";
import API from "helpers/api";
import useDidMountEffect from "hooks/useDidMountEffect";
import usePrevious from "hooks/usePrevious";

const successColor = "#24BA78";
const errorColor = "#E21313";

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	box: {
		marginTop: 16,
	},
	tableContainer: {
		[media]: {
			whiteSpace: "nowrap",
		},
	},
	tableHead: {
		backgroundColor: "#D2D2D9",
		border: "1px solid",
		width: "100%",
	},
}));

const header = (title, errorData, errors, elementID) => (
	<span>
		{title} &nbsp;&nbsp;
		{errors?.total > 0 && (
			<span
				style={{
					color: errors.total === errors.resolved ? successColor : errorColor,
				}}
			>
				{elementID !== "roleID" && `(${errorData} Errors Resolved)`}
			</span>
		)}
	</span>
);

const norm = (v) => (v ?? "").trim().toLowerCase();

// A row counts as resolved once a destination has been provided — either by
// adding a new value (newName) or by selecting an existing one (elementID).
// We intentionally do NOT require the selected destination's name to match the
// translated name. When the translated name has no matching destination the
// auto-match below leaves the dropdown unselected, so any value present here is
// a deliberate user choice and should resolve the row even if it's unmapped
// (i.e. doesn't match the translation).
const isRowResolved = (row, elementID) => {
	if (row.newName !== null) return true;
	return row[elementID] !== null;
};

const Elements = ({
	name,
	title,
	errors,
	getApi,
	patchApi,
	siteAppID,
	mainData = [],
	errorName,
	modelName,
	elementID,
	onErrorResolve,
	disableInput,
	access,
	translations,
}) => {
	const showTranslationColumn =
		translations && Object.keys(translations).length > 0;
	const { classes, cx } = useStyles();
	const [dropDown, setDropDown] = useState([]);
	const [updatedDetails, setUpdatedDetails] = useState({
		data: mainData,
		selectedRow: 0,
	});

	useEffect(() => {
		if (siteAppID) {
			API.get(getApi + `?siteAppId=${siteAppID}`).then((res) =>
				setDropDown(res.data)
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteAppID]);
	const [prevData, setPreviousValue] = usePrevious(updatedDetails);

	// IDs of rows the frontend auto-matched from a translation. Used to re-map
	// them on a subsequent translation while leaving the user's own picks alone.
	const autoMatchedIdsRef = useRef(new Set());

	const setErrorResolve = (datas, y) => {
		// If input value is null i.e. deleted then subtract else add

		// A manual change (select / Add New / delete) makes this row user-owned —
		// drop it from the auto-match set so future translations don't override it.
		autoMatchedIdsRef.current.delete(y.id);

		const uData = updatedDetails.data.map((x) => {
			return x.id === y.id ? { ...x, ...datas } : x;
		});
		setUpdatedDetails({ data: uData, selectedRow: y.id });
	};
	useDidMountEffect(() => {
		const uData = prevData.data.map((x) => {
			return x.id === updatedDetails.selectedRow
				? updatedDetails.data.find((y) => y.id === x.id)
				: x;
		});
		setPreviousValue({ data: uData, selectedRow: updatedDetails.selectedRow });
		const resolvedCount = uData.filter((row) =>
			isRowResolved(row, elementID)
		).length;
		onErrorResolve(uData, modelName, errorName, elementID, resolvedCount);
	}, [updatedDetails]);

	// Auto-match against the destination dropdown using translated names. Runs
	// EVERY time a translation completes (the `translations` object changes) as
	// well as when the dropdown first loads — so re-translating (e.g. switching
	// language) resets the auto-matched dropdowns and maps the values again.
	// Targets three row states:
	//   1. Unresolved rows (no destination, no NewName) — pick the translated
	//      match if one exists; otherwise leave the dropdown unselected.
	//   2. Rows the backend pre-matched at upload time to a destination whose
	//      name equals the SOURCE name (its English auto-match).
	//   3. Rows this effect auto-matched from an earlier translation (tracked in
	//      autoMatchedIdsRef) — re-map them against the new translation.
	// For each, re-point to the new translated destination if one exists,
	// otherwise CLEAR the selection so the dropdown is left unselected and the
	// user is prompted to pick or Add New.
	// Rows where the user has explicitly picked a destination, or clicked Add
	// New, are left alone (they're removed from autoMatchedIdsRef on change).
	useEffect(() => {
		if (!translations || Object.keys(translations).length === 0) return;
		if (!dropDown || dropDown.length === 0) return;

		const dropDownByName = new Map(dropDown.map((d) => [norm(d.name), d]));
		const dropDownById = new Map(dropDown.map((d) => [d.id, d]));

		const matchPromises = updatedDetails.data
			.filter((row) => {
				if (row.newName !== null) return false;
				if (row[elementID] === null) return true;
				if (autoMatchedIdsRef.current.has(row.id)) return true;
				const currentDest = dropDownById.get(row[elementID]);
				return currentDest && norm(currentDest.name) === norm(row.name);
			})
			.map((row) => {
				const translated = translations[row.name];
				const match = translated
					? dropDownByName.get(norm(translated))
					: null;
				// No destination matches the translated name → clear the dropdown
				// (value = null) so it's left unselected. Otherwise re-point to the
				// translated match. Skip rows already on the right value.
				const targetValue = match ? match.id : null;
				if (targetValue === row[elementID]) return null;
				return API.patch(`${patchApi}/${row.id}`, [
					{ op: "replace", path: elementID, value: targetValue },
				])
					.then((res) => ({ row, payload: res.data, targetValue }))
					.catch(() => null);
			})
			.filter(Boolean);

		if (matchPromises.length === 0) return;

		Promise.all(matchPromises).then((results) => {
			const hits = results.filter(Boolean);
			if (hits.length === 0) return;
			// Track which rows are now frontend auto-matched so the next
			// translation can re-map them; clear the flag for rows we reset.
			hits.forEach(({ row, targetValue }) => {
				if (targetValue !== null) autoMatchedIdsRef.current.add(row.id);
				else autoMatchedIdsRef.current.delete(row.id);
			});
			const merged = updatedDetails.data.map((x) => {
				const hit = hits.find((r) => r.row.id === x.id);
				return hit ? { ...x, ...hit.payload } : x;
			});
			// Skip the per-row useDidMountEffect dance — we have multiple rows
			// updating at once. Sync local state and bubble straight up to the
			// parent so the error counter recomputes.
			const resolvedCount = merged.filter((row) =>
				isRowResolved(row, elementID)
			).length;
			setUpdatedDetails({ data: merged, selectedRow: -1 });
			setPreviousValue({ data: merged, selectedRow: -1 });
			onErrorResolve(merged, modelName, errorName, elementID, resolvedCount);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [translations, dropDown]);
	return (
		<AccordionBox
			accordionClass={classes.box}
			defaultExpanded={false}
			title={header(
				title,
				`${errors?.resolved}/${errors?.total}`,
				errors,
				elementID
			)}
		>
			<Table className={classes.tableContainer}>
				<TableHead className={classes.tableHead}>
					<TableRow>
						<TableCell>Source {name}</TableCell>
						{showTranslationColumn && (
							<TableCell>Translated {name}</TableCell>
						)}
						<TableCell>Destination {name} (Please Select)</TableCell>
						<TableCell>Destination {name} (Add New)</TableCell>
						<TableCell>Resolved ?</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{updatedDetails.data.map((x) => (
						<Row
							key={x.id}
							dropDown={dropDown.map((x) => ({ label: x.name, id: x.id }))}
							x={x}
							patchApi={patchApi}
							setErrorResolve={(data) => setErrorResolve(data, x)}
							elementID={elementID}
							disableInput={disableInput}
							access={access}
							translatedText={
								showTranslationColumn ? translations[x.name] : undefined
							}
							showTranslationColumn={showTranslationColumn}
							resolved={isRowResolved(x, elementID)}
						/>
					))}
				</TableBody>
			</Table>
		</AccordionBox>
	);
};

export default Elements;
